import axios, { AxiosResponse } from 'axios';
import { uniq } from 'lodash';

import { cleanupFilters } from './transferables_filters';
import {
  Transferable,
  TransferablesCustomer,
  TransferablesFilters,
  TransferablesRegion,
  TransferablesWarehouse,
  TransferRequestForm,
} from './types';

export const regionsRequest = (): Promise<AxiosResponse<TransferablesRegion[]>> =>
  axios.get<TransferablesRegion[]>('/regions.json');

export const warehousesRequest = (): Promise<AxiosResponse<TransferablesWarehouse[]>> =>
  axios.get<TransferablesWarehouse[]>('/warehouses.json');

export const transferablesRequest = (filters: TransferablesFilters): Promise<AxiosResponse<Transferable[]>> =>
  axios.get<Transferable[]>('/transferables.json', { params: cleanupFilters(filters) });

const accountIdsFromTransferables = (transferables: Transferable[]) =>
  uniq(transferables.flatMap((transferable) => transferable.pallets.flatMap((pallet) => pallet.account_ids)));

export const customersRequest = (
  transferables: Transferable[],
): Promise<AxiosResponse<TransferablesCustomer[]>> | undefined => {
  const accountIds = accountIdsFromTransferables(transferables);
  if (!accountIds.length) {
    return undefined;
  }

  return axios.get<TransferablesCustomer[]>('/customers.json', {
    params: { account_ids: accountIds },
  });
};

export const attachCustomers = (transferables: Transferable[], customers: TransferablesCustomer[]): Transferable[] =>
  transferables.map((transferable) => ({
    ...transferable,
    pallets: transferable.pallets.map((pallet) => ({
      ...pallet,
      customers: customers.filter((customer) => pallet.account_ids.includes(customer.account_id)),
    })),
  }));

export const createTransferRequest = (form: TransferRequestForm) => axios.post('/bulk/transferrequest.json', form);

export const extractErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string; error?: string } | string | undefined;
    if (typeof data === 'string' && data.length > 0) {
      return data;
    }
    if (data && typeof data === 'object') {
      return data.message ?? data.error ?? 'An unknown error occurred. Please contact technical support.';
    }
  }

  return 'An unknown error occurred. Please contact technical support.';
};
